import axios from 'axios';
import { toast } from 'react-toastify';
import { BACKEND_URL } from './constants';

const updateDownloadToast = (toastId, render) => {
    if (!toast.isActive(toastId)) return;
    toast.update(toastId, { render, type: 'info', autoClose: false });
};

const dismissDownloadToast = (toastId) => {
    if (!toast.isActive(toastId)) return;
    toast.dismiss(toastId);
};

const formatBytes = (bytes) => {
    if (!bytes || bytes < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const serializeParams = (params = {}) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null && item !== '') {
                    searchParams.append(key, item);
                }
            });
            return;
        }

        searchParams.append(key, value);
    });

    return searchParams.toString();
};

export const downloadZipFileWithProgress = async (url, token, options = {}, fileName = 'download.zip') => {
    let downloadToastId = null;

    try {
        downloadToastId = toast.info('Download iniciado...', { autoClose: false });

        const config = {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
            },
            paramsSerializer: options.paramsSerializer || { serialize: serializeParams },
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
                const total = progressEvent.total;
                const hasTotal = Number.isFinite(total) && total > 0;
                const progressText = hasTotal
                    ? `A descarregar: ${Math.round((progressEvent.loaded * 100) / total)}%`
                    : `A descarregar... ${formatBytes(progressEvent.loaded)}`;
                updateDownloadToast(downloadToastId, progressText);
            }
        };

        const response = await axios.get(url, config);

        // Check if the response is actually a ZIP file
        const contentType = response.headers['content-type'];
        if (!contentType.includes('zip') && !contentType.includes('octet-stream')) {
            throw new Error('Invalid file type received. Expected ZIP file.');
        }

        const fileBlob = new Blob([response.data], {
            type: 'application/zip' // Set correct MIME type for ZIP
        });
        const downloadUrl = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Check if response has a Content-Disposition header with a filename
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch && filenameMatch.length === 2) {
                // Ensure filename ends with .zip
                fileName = filenameMatch[1].endsWith('.zip')
                    ? filenameMatch[1]
                    : `${filenameMatch[1]}.zip`;
            }
        }

        // Ensure default filename ends with .zip
        if (!fileName.endsWith('.zip')) {
            fileName = `${fileName}.zip`;
        }

        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Clean up the URL object
        window.URL.revokeObjectURL(downloadUrl);

        dismissDownloadToast(downloadToastId);

        return response.data;
    } catch (error) {
        dismissDownloadToast(downloadToastId);
        toast.error('Error downloading ZIP file');
        console.error('Error downloading file:', error);
        throw error;
    }
};

export const downloadFileWithProgress = async (url, token, options = {}, fileName = 'download.csv') => {
    let downloadToastId = null;

    try {
        downloadToastId = toast.info('Download iniciado...', { autoClose: false });

        const config = {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
            },
            paramsSerializer: options.paramsSerializer || { serialize: serializeParams },
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
                const total = progressEvent.total;
                const hasTotal = Number.isFinite(total) && total > 0;
                const progressText = hasTotal
                    ? `A descarregar: ${Math.round((progressEvent.loaded * 100) / total)}%`
                    : `A descarregar... ${formatBytes(progressEvent.loaded)}`;
                updateDownloadToast(downloadToastId, progressText);
            }
        };

        const response = await axios.get(url, config);

        const fileBlob = new Blob([response.data], { type: 'text/csv' });
        const downloadUrl = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Check if response has a Content-Disposition header with a filename
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch && filenameMatch.length === 2) {
                fileName = filenameMatch[1];
            }
        }

        // Set the dynamic filename or fallback to the default
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();
        link.remove();

        dismissDownloadToast(downloadToastId);

        return response.data;
    } catch (error) {
        dismissDownloadToast(downloadToastId);
        toast.error('Erro ao fazer download do ficheiro');
        console.error('Erro ao ler dados:', error);
        throw error;
    }
};

export const handleContractDownload = async (identifier, token) => {
    try {
        const response = await axios({
            url: `${BACKEND_URL}/contracts/${identifier}/download/`,
            method: 'GET',
            responseType: 'blob',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
            }
        });

        // Get filename from Content-Disposition header if available
        const contentDisposition = response.headers['content-disposition'];
        let filename = `contract-${identifier}.pdf`; // Default filename
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }

        // Create a URL for the blob
        const url = window.URL.createObjectURL(new Blob([response.data]));

        // Create a temporary link element and trigger the download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading contract:', error);
        // Handle error appropriately (e.g., show user notification)
    }
};

export const postDataClient = async (url, data, token) => {
    try {
        const formData = new FormData();

        // Extract file from contract
        const file = data.contract;

        // Append JSON data, excluding 'contract'
        Object.keys(data).forEach(key => {
            if (key !== 'contract') {
                formData.append(key, data[key]);
            }
        });

        // Append file with 'file' key
        if (file) {
            formData.append('file', file);
        }

        const headers = {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
        };

        const response = await axios.post(url, formData, {
            headers,
            transformRequest: [function (data, headers) {
                return data;
            }],
        });

        console.log('response', response);
        return response;
    } catch (error) {
        console.error('Error submitting data:', error);
        throw error;
    }
};

export const uploadFileWithProgress = async (url, file, token, year = null, month = null) => {
    const formData = new FormData();

    formData.append('file', file);

    if (year) {
        formData.append('year', year);
    }
    if (month) {
        formData.append('month', month);
    }

    try {
        const response = await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`
            },
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};


/**
 * Send a POST request with JSON or multipart data
 * @param {string} url - The endpoint URL.
 * @param {object|FormData} data - The data to send in the request body. Can be JSON or FormData.
 * @param {string} token - The authorization token.
 */
export const postData = async (url, data, token) => {
    try {
        const isMultipart = data instanceof FormData || (typeof data === 'object' && 'file' in data);

        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
            'accept': 'application/json',
        };

        const response = await axios.post(url, data, { headers });
        console.log('response', response);
        return response;
    } catch (error) {
        console.error('Error submitting data:', error);
        throw error;
    }
};

/**
 * Send a GET request with optional query parameters
 * @param {string} url - The endpoint URL.
 * @param {object} params - The query parameters to send in the request.
 * @param {string} token - The authorization token.
 */
export const getData = async (url, params = {}, token) => {
    try {
        const response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            params
        });
        return response.data; // Return the data part of the response
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};
